// BotsTab.jsx - Modern Bot Control Panel with Date Selection & Bulk Actions
// Version: 3.3.0 - Added live logs viewer for dev mode bots

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Bot, RefreshCw, Play, Square, RotateCcw, BarChart3,
    Activity, Clock, WifiOff, ChevronLeft, ChevronRight,
    Phone, PhoneForwarded, Voicemail, PhoneOff, PhoneMissed,
    UserX, Ban, CheckSquare, Square as SquareIcon, AlertTriangle,
    Server, ChevronDown, ChevronUp, Link2, X, Check, Power, PowerOff,
    Cpu, HardDrive, ExternalLink, ToggleLeft, ToggleRight, RefreshCcw, FileWarning,
    Copy, Mic, Pause, Loader2, AlertCircle, User, Radio, Terminal
} from 'lucide-react'
import { monitoringApi, voiceSetApi, machineApi } from '../services/api'
import { useRef } from 'react'
import LinkVMModal from './LinkVMModal'
import CrashReportModal from './CrashReportModal'
import LiveLogsModal from './LiveLogsModal'
import './BotsTab.css'

// Status configuration
const STATUS_CONFIG = {
    active: { label: 'Active', color: '#10b981', icon: Activity },
    waiting: { label: 'Logged Out', color: '#f59e0b', icon: Clock },
    offline: { label: 'Offline', color: '#6b7280', icon: WifiOff },
    running: { label: 'Active', color: '#10b981', icon: Activity },
    dial_level_0: { label: 'Dial Lvl 0', color: '#ef4444', icon: AlertTriangle },
    login_retry: { label: 'Login Retry', color: '#f59e0b', icon: AlertTriangle },
    login_error: { label: 'Login Error', color: '#ef4444', icon: X }
}

// Date presets
const DATE_PRESETS = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: '7days', label: '7 Days' },
    { key: 'custom', label: 'Custom' }
]

// Progress messages
const START_PROGRESS = ['Sending...', 'Logging in...', 'Activating...']
const STOP_PROGRESS = ['Sending...', 'Logging out...', 'Waiting...']

const PAGE_SIZES = [25, 50, 100]

const formatTimeAgo = (date) => {
    if (!date) return '-'
    const diff = Math.abs(Math.floor((new Date() - new Date(date)) / 1000))
    if (diff < 60) return `${diff}s`
    if (diff < 3600) return `${Math.floor(diff / 60)}m`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`
    return `${Math.floor(diff / 86400)}d`
}

const getDateString = (date) => date.toISOString().split('T')[0]

// Toast notification component
function Toast({ message, type, onClose }) {
    useEffect(() => {
        const timer = setTimeout(onClose, 4000)
        return () => clearTimeout(timer)
    }, [onClose])

    return (
        <div className={`bt-toast bt-toast-${type}`}>
            {type === 'success' && <Check size={16} />}
            {type === 'error' && <X size={16} />}
            {type === 'info' && <Server size={16} />}
            <span>{message}</span>
            <button onClick={onClose}><X size={14} /></button>
        </div>
    )
}

// Confirmation dialog component with optional checkboxes
function ConfirmDialog({ title, message, onConfirm, onCancel, checkboxes: initialCheckboxes = [], confirmText = "Yes, Continue" }) {
    const [checkboxStates, setCheckboxStates] = useState(
        initialCheckboxes.map(cb => cb.checked ?? false)
    )

    const handleCheckboxChange = (idx, checked) => {
        setCheckboxStates(prev => {
            const next = [...prev]
            next[idx] = checked
            return next
        })
    }

    const handleConfirm = () => {
        onConfirm(checkboxStates)
    }

    return (
        <div className="bt-confirm-overlay" onClick={onCancel}>
            <div className="bt-confirm-dialog" onClick={e => e.stopPropagation()}>
                <div className="bt-confirm-title">{title}</div>
                <div className="bt-confirm-message">{message}</div>
                {initialCheckboxes.length > 0 && (
                    <div className="bt-confirm-checkboxes">
                        {initialCheckboxes.map((cb, idx) => (
                            <label key={idx} className="bt-confirm-checkbox">
                                <input
                                    type="checkbox"
                                    checked={checkboxStates[idx]}
                                    onChange={(e) => handleCheckboxChange(idx, e.target.checked)}
                                />
                                <span>{cb.label}</span>
                            </label>
                        ))}
                    </div>
                )}
                <div className="bt-confirm-actions">
                    <button className="bt-confirm-no" onClick={onCancel}>Cancel</button>
                    <button className="bt-confirm-yes" onClick={handleConfirm}>{confirmText}</button>
                </div>
            </div>
        </div>
    )
}

export default function BotsTab({ dialerId }) {
    const navigate = useNavigate()
    const [bots, setBots] = useState([])
    const [loading, setLoading] = useState(true)
    const [commandState, setCommandState] = useState({})
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(50)
    const [selectedStatus, setSelectedStatus] = useState('')

    // Date selection
    const [datePreset, setDatePreset] = useState('today')
    const [customFrom, setCustomFrom] = useState('')
    const [customTo, setCustomTo] = useState('')
    const [showDatePicker, setShowDatePicker] = useState(false)

    // Bulk selection
    const [selectedBots, setSelectedBots] = useState(new Set())
    const [bulkLoading, setBulkLoading] = useState(false)

    // Expanded VM row
    const [expandedBot, setExpandedBot] = useState(null)
    const [vmStatus, setVmStatus] = useState({}) // { sipId: { status: 'running'|'stopped', loading: false } }

    // Link VM Modal
    const [linkVMBot, setLinkVMBot] = useState(null)

    // v3.95: Crash Report Modal
    const [crashReportBot, setCrashReportBot] = useState(null)

    // v3.96: Live Logs Modal (dev mode only)
    const [liveLogsBot, setLiveLogsBot] = useState(null)

    // Toast notifications
    const [toasts, setToasts] = useState([])

    // Confirmation dialog
    const [confirmAction, setConfirmAction] = useState(null)

    // Servers section
    const [servers, setServers] = useState([])
    const [serversCollapsed, setServersCollapsed] = useState(false)
    const [serversLoading, setServersLoading] = useState(true)

    // Voice Set Pool Modal
    const [voiceSets, setVoiceSets] = useState([])
    const [showVoiceSetModal, setShowVoiceSetModal] = useState(false)
    const [voiceSetEditBot, setVoiceSetEditBot] = useState(null)
    const [voiceSetBulkBots, setVoiceSetBulkBots] = useState([])
    const [voiceSetBulkMode, setVoiceSetBulkMode] = useState(false)
    const [voiceSetEditLoading, setVoiceSetEditLoading] = useState(false)
    const [voiceSetSaving, setVoiceSetSaving] = useState(false)
    const [licenseData, setLicenseData] = useState({ voice_set_pool: [] })
    const [playingVoiceSet, setPlayingVoiceSet] = useState(null)
    const [audioLoading, setAudioLoading] = useState(null)
    const audioRef = useRef(new Audio())

    // Transcriber Modal
    const [showTranscriberModal, setShowTranscriberModal] = useState(false)
    const [transcriberEditBot, setTranscriberEditBot] = useState(null)
    const [transcriberBulkBots, setTranscriberBulkBots] = useState([])
    const [transcriberBulkMode, setTranscriberBulkMode] = useState(false)
    const [transcriberSaving, setTranscriberSaving] = useState(false)
    const [selectedTranscriber, setSelectedTranscriber] = useState('VOSK')

    // Show toast
    const showToast = useCallback((message, type = 'info') => {
        const id = Date.now()
        setToasts(prev => [...prev, { id, message, type }])
    }, [])

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id))
    }, [])

    // Calculate date range
    const getDateRange = useCallback(() => {
        const today = new Date()
        switch (datePreset) {
            case 'today':
                return { from: getDateString(today), to: getDateString(today) }
            case 'yesterday':
                const yesterday = new Date(today - 24 * 60 * 60 * 1000)
                return { from: getDateString(yesterday), to: getDateString(yesterday) }
            case '7days':
                const weekAgo = new Date(today - 7 * 24 * 60 * 60 * 1000)
                return { from: getDateString(weekAgo), to: getDateString(today) }
            case 'custom':
                return { from: customFrom || getDateString(today), to: customTo || getDateString(today) }
            default:
                return { from: getDateString(today), to: getDateString(today) }
        }
    }, [datePreset, customFrom, customTo])

    // Fetch bots with stats
    // silent = true means no loading indicator (used for auto-refresh)
    const fetchBots = useCallback(async (silent = false) => {
        if (!silent) setLoading(true)
        try {
            const botsRes = await monitoringApi.getBotsByDialer(dialerId)
            const botList = botsRes.data?.bots || []

            const dateRange = getDateRange()
            const sipStats = {}

            try {
                const statsRes = await monitoringApi.getDispositions({
                    dialer_id: dialerId,
                    from: dateRange.from,
                    to: dateRange.to,
                    limit: 10000
                })

                const dispositionsArray = statsRes.data?.data || []
                for (const call of dispositionsArray) {
                    if (!sipStats[call.sip_id]) {
                        sipStats[call.sip_id] = { total: 0, xfer: 0, am: 0, ni: 0, na: 0, dnc: 0, dnq: 0 }
                    }
                    const stats = sipStats[call.sip_id]
                    stats.total++
                    const disp = (call.disposition || '').toLowerCase()
                    if (disp.includes('transfer') || call.was_transferred) stats.xfer++
                    else if ((disp.includes('answer') && disp.includes('machine')) || disp.includes('voicemail') || disp.includes('am ')) stats.am++
                    else if (disp.includes('not') && disp.includes('interest')) stats.ni++
                    else if (disp.includes('no') && disp.includes('answer')) stats.na++
                    else if ((disp.includes('do') && disp.includes('not') && disp.includes('call')) || disp === 'dnc') stats.dnc++
                    else if ((disp.includes('not') && disp.includes('qualif')) || disp === 'dnq' || disp.includes('age')) stats.dnq++
                }
            } catch (err) {
                console.warn('Failed to fetch call stats:', err)
            }

            const enrichedBots = botList.map(bot => ({
                ...bot,
                calls: sipStats[bot.sip_id]?.total || 0,
                xfer: sipStats[bot.sip_id]?.xfer || 0,
                am: sipStats[bot.sip_id]?.am || 0,
                ni: sipStats[bot.sip_id]?.ni || 0,
                na: sipStats[bot.sip_id]?.na || 0,
                dnc: sipStats[bot.sip_id]?.dnc || 0,
                dnq: sipStats[bot.sip_id]?.dnq || 0
            }))

            setBots(enrichedBots)

            const activeStatuses = ['active', 'running', 'dial_level_0']
            Object.entries(commandState).forEach(([sipId, cmdState]) => {
                const bot = enrichedBots.find(b => b.sip_id === sipId)
                if (bot && ((cmdState.cmd === 'start' && activeStatuses.includes(bot.effective_status)) ||
                    (cmdState.cmd === 'stop' && !activeStatuses.includes(bot.effective_status)))) {
                    setCommandState(prev => { const n = { ...prev }; delete n[sipId]; return n })
                }
            })
        } catch (err) {
            console.error('Failed to load bots:', err)
            if (!silent) setBots([])
        } finally {
            if (!silent) setLoading(false)
        }
    }, [dialerId, getDateRange, commandState])

    useEffect(() => {
        fetchBots()
        const interval = setInterval(() => fetchBots(true), 10000) // silent refresh
        return () => clearInterval(interval)
    }, [fetchBots])

    // Fetch servers that have VMs for this dialer
    const fetchServers = useCallback(async () => {
        setServersLoading(true)
        try {
            const res = await monitoringApi.getServersByDialer(dialerId)
            setServers(res.data?.servers || [])
        } catch (err) {
            console.error('Failed to load servers:', err)
            setServers([])
        } finally {
            setServersLoading(false)
        }
    }, [dialerId])

    useEffect(() => {
        fetchServers()
        const interval = setInterval(fetchServers, 60000) // Refresh every 60 seconds
        return () => clearInterval(interval)
    }, [fetchServers])

    // Clear selection and command state when dialerId changes to prevent cross-dialer issues
    useEffect(() => {
        setSelectedBots(new Set())
        setCommandState({})
        setCurrentPage(1)
    }, [dialerId])

    // ============================================
    // VOICE SET POOL FUNCTIONS
    // ============================================

    // Fetch voice sets for dropdown
    const fetchVoiceSets = useCallback(async () => {
        try {
            const res = await voiceSetApi.getAll()
            setVoiceSets(res.data || [])
        } catch (err) {
            console.error('Failed to fetch voice sets:', err)
        }
    }, [])

    // Open voice set modal for single bot
    const openVoiceSetModal = async (bot) => {
        if (!bot.license_id) {
            showToast('Bot has no license configured', 'error')
            return
        }
        setVoiceSetEditBot(bot)
        setVoiceSetBulkMode(false)
        setShowVoiceSetModal(true)
        setVoiceSetEditLoading(true)

        // Fetch voice sets if not loaded
        if (voiceSets.length === 0) {
            await fetchVoiceSets()
        }

        // Fetch current license voice pool
        try {
            const res = await monitoringApi.getLicenseVoicePool(bot.license_id)
            if (res.data?.success) {
                setLicenseData({
                    voice_set_pool: res.data.license.voice_set_pool || []
                })
            }
        } catch (err) {
            console.error('Failed to fetch license voice pool:', err)
            showToast('Failed to load voice settings', 'error')
        }
        setVoiceSetEditLoading(false)
    }

    // Open voice set modal for bulk edit
    const openBulkVoiceSetModal = async () => {
        const sipIds = [...selectedBots]
        const botsToEdit = bots.filter(b => sipIds.includes(b.sip_id) && b.license_id)

        if (botsToEdit.length === 0) {
            showToast('No bots with licenses selected', 'error')
            return
        }

        setVoiceSetBulkBots(botsToEdit)
        setVoiceSetBulkMode(true)
        setShowVoiceSetModal(true)
        setVoiceSetEditLoading(true)

        // Fetch voice sets if not loaded
        if (voiceSets.length === 0) {
            await fetchVoiceSets()
        }

        // Fetch voice pools from all selected bots and find intersection
        try {
            const allVoiceSets = []
            for (const bot of botsToEdit) {
                const res = await monitoringApi.getLicenseVoicePool(bot.license_id)
                if (res.data?.success && res.data.license.voice_set_pool) {
                    allVoiceSets.push(res.data.license.voice_set_pool)
                }
            }

            // Find intersection (voice sets common to all bots)
            let commonVoiceSets = []
            if (allVoiceSets.length > 0) {
                commonVoiceSets = allVoiceSets.reduce((acc, curr) =>
                    acc.filter(id => curr.includes(id))
                )
            }

            setLicenseData({ voice_set_pool: commonVoiceSets })
        } catch (err) {
            console.error('Failed to fetch voice sets for bulk edit:', err)
            setLicenseData({ voice_set_pool: [] })
        }
        setVoiceSetEditLoading(false)
    }

    // Close voice set modal
    const closeVoiceSetModal = () => {
        setShowVoiceSetModal(false)
        setVoiceSetEditBot(null)
        setVoiceSetBulkBots([])
        setVoiceSetBulkMode(false)
        setLicenseData({ voice_set_pool: [] })
        // Stop any playing audio
        if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.src = ''
        }
        setPlayingVoiceSet(null)
    }

    // Save voice set pool changes
    const saveVoiceSetChanges = async () => {
        setVoiceSetSaving(true)

        if (voiceSetBulkMode) {
            // Bulk edit
            let successCount = 0
            let failCount = 0

            for (const bot of voiceSetBulkBots) {
                try {
                    const res = await monitoringApi.updateLicenseVoicePool(
                        bot.license_id,
                        licenseData.voice_set_pool
                    )
                    if (res.data?.success) successCount++
                    else failCount++
                } catch (err) {
                    console.error(`Failed to update ${bot.sip_id}:`, err)
                    failCount++
                }
            }

            if (successCount > 0) {
                showToast(`Voice sets updated for ${successCount} bot${successCount > 1 ? 's' : ''}${failCount > 0 ? `, ${failCount} failed` : ''}`, 'success')
                setTimeout(() => {
                    showToast('Restart VMs to apply voice set changes', 'info')
                }, 500)
            } else {
                showToast('Failed to update bots', 'error')
            }

            closeVoiceSetModal()
            setSelectedBots(new Set())
        } else {
            // Single bot edit
            if (!voiceSetEditBot) return

            try {
                const res = await monitoringApi.updateLicenseVoicePool(
                    voiceSetEditBot.license_id,
                    licenseData.voice_set_pool
                )

                if (res.data?.success) {
                    showToast('Voice sets updated successfully', 'success')
                    setTimeout(() => {
                        showToast('Restart VM to apply voice set changes', 'info')
                    }, 500)
                    closeVoiceSetModal()
                } else {
                    showToast(res.data?.error || 'Failed to update voice sets', 'error')
                }
            } catch (err) {
                console.error('Failed to save voice sets:', err)
                showToast('Failed to save voice sets', 'error')
            }
        }
        setVoiceSetSaving(false)
    }

    // Toggle voice set in pool
    const toggleVoiceSet = (vsId) => {
        setLicenseData(prev => {
            const pool = prev.voice_set_pool || []
            if (pool.includes(vsId)) {
                return { ...prev, voice_set_pool: pool.filter(id => id !== vsId) }
            } else {
                return { ...prev, voice_set_pool: [...pool, vsId] }
            }
        })
    }

    // Preview voice set audio
    const previewVoiceSet = async (vsId, e) => {
        e.stopPropagation() // Don't toggle selection

        // If already playing this voice set, stop it
        if (playingVoiceSet === vsId) {
            audioRef.current.pause()
            audioRef.current.src = ''
            setPlayingVoiceSet(null)
            return
        }

        setAudioLoading(vsId)
        setPlayingVoiceSet(vsId)

        try {
            // Find a sample file from this voice set
            const vs = voiceSets.find(v => v.id === vsId)
            if (vs?.sample_url) {
                const audio = audioRef.current
                // Reset event handlers first to prevent stale callbacks
                audio.onended = null
                audio.onerror = null
                // Set source and load
                audio.src = vs.sample_url
                audio.load()
                // Set up event handlers
                audio.onended = () => setPlayingVoiceSet(null)
                // Play and handle errors via promise rejection
                await audio.play()
            } else {
                showToast('No sample audio available', 'info')
                setPlayingVoiceSet(null)
            }
        } catch (err) {
            console.error('Failed to preview voice set:', err)
            showToast('Failed to play audio', 'error')
            setPlayingVoiceSet(null)
        }
        setAudioLoading(null)
    }

    // Transcriber Modal Functions
    const TRANSCRIBER_OPTIONS = [
        { id: 'VOSK', label: 'VOSK', desc: 'Fast, lightweight' },
        { id: 'VOSK_V15L', label: 'V15L', desc: 'Accurate, larger model' },
        { id: 'HYBRID', label: 'HYBRID', desc: 'VOSK + Whisper per step' },
        { id: 'WHISPER_CPU', label: 'W-CPU', desc: 'Whisper Tiny (Local)' }
    ]

    const openTranscriberModal = (bot) => {
        setTranscriberEditBot(bot)
        setTranscriberBulkMode(false)
        setSelectedTranscriber(bot.transcriber_type || 'VOSK')
        setShowTranscriberModal(true)
    }

    const openBulkTranscriberModal = () => {
        if (selectedBots.size === 0) {
            showToast('Select bots first', 'error')
            return
        }
        const botsToEdit = bots.filter(b => selectedBots.has(b.sip_id))
        setTranscriberBulkBots(botsToEdit)
        setTranscriberBulkMode(true)
        // Find common transcriber or default to VOSK
        const types = [...new Set(botsToEdit.map(b => b.transcriber_type || 'VOSK'))]
        setSelectedTranscriber(types.length === 1 ? types[0] : 'VOSK')
        setShowTranscriberModal(true)
    }

    const closeTranscriberModal = () => {
        setShowTranscriberModal(false)
        setTranscriberEditBot(null)
        setTranscriberBulkBots([])
        setTranscriberBulkMode(false)
    }

    const saveTranscriberChange = async () => {
        setTranscriberSaving(true)
        try {
            if (transcriberBulkMode) {
                // Bulk update
                for (const bot of transcriberBulkBots) {
                    await monitoringApi.updateLicenseTranscriber(bot.license_id, selectedTranscriber)
                }
                showToast(`Updated transcriber for ${transcriberBulkBots.length} bots`, 'success')
                // Update local state
                setBots(prev => prev.map(b =>
                    transcriberBulkBots.find(tb => tb.sip_id === b.sip_id)
                        ? { ...b, transcriber_type: selectedTranscriber }
                        : b
                ))
            } else {
                // Single update
                await monitoringApi.updateLicenseTranscriber(transcriberEditBot.license_id, selectedTranscriber)
                showToast('Transcriber updated', 'success')
                // Update local state
                setBots(prev => prev.map(b =>
                    b.sip_id === transcriberEditBot.sip_id
                        ? { ...b, transcriber_type: selectedTranscriber }
                        : b
                ))
            }
            closeTranscriberModal()
        } catch (err) {
            console.error('Failed to update transcriber:', err)
            showToast('Failed to update transcriber', 'error')
        }
        setTranscriberSaving(false)
    }

    // Progress animation with timeout detection
    useEffect(() => {
        const timers = []
        Object.entries(commandState).forEach(([sipId, cmdState]) => {
            // Skip if already showing error
            if (cmdState.error) return

            if (cmdState.progress < 2) {
                // Normal progress animation
                timers.push(setTimeout(() => {
                    setCommandState(prev => {
                        if (!prev[sipId]) return prev
                        return {
                            ...prev,
                            [sipId]: {
                                ...prev[sipId],
                                progress: prev[sipId].progress + 1,
                                status: (cmdState.cmd === 'start' ? START_PROGRESS : STOP_PROGRESS)[prev[sipId].progress + 1]
                            }
                        }
                    })
                }, 2500))
            } else if (cmdState.progress === 2 && !cmdState.timeoutSet) {
                // Set timeout for error after 15 seconds at "Activating..." stage
                timers.push(setTimeout(() => {
                    setCommandState(prev => {
                        if (!prev[sipId] || prev[sipId].error) return prev
                        return {
                            ...prev,
                            [sipId]: {
                                ...prev[sipId],
                                error: true,
                                status: 'No response - check VM/browser'
                            }
                        }
                    })
                }, 15000))
                // Mark that timeout is set
                setCommandState(prev => {
                    if (!prev[sipId]) return prev
                    return {
                        ...prev,
                        [sipId]: { ...prev[sipId], timeoutSet: true }
                    }
                })
            }
        })
        return () => timers.forEach(clearTimeout)
    }, [commandState])

    // Fetch VM status when expanding
    const fetchVMStatus = useCallback(async (bot) => {
        if (!bot.server_ip || !bot.vmid) return

        setVmStatus(prev => ({ ...prev, [bot.sip_id]: { ...prev[bot.sip_id], loading: true } }))

        try {
            const res = await monitoringApi.vmStatus(bot.server_ip, bot.vmid)
            if (res.data.success) {
                setVmStatus(prev => ({
                    ...prev,
                    [bot.sip_id]: { status: res.data.status, loading: false }
                }))
            }
        } catch (err) {
            console.error('Failed to fetch VM status:', err)
            setVmStatus(prev => ({ ...prev, [bot.sip_id]: { status: 'unknown', loading: false } }))
        }
    }, [])

    // Toggle expanded row
    const toggleExpand = useCallback((bot) => {
        if (expandedBot === bot.sip_id) {
            setExpandedBot(null)
        } else {
            setExpandedBot(bot.sip_id)
            if (bot.vmid && bot.server_ip) {
                fetchVMStatus(bot)
            }
        }
    }, [expandedBot, fetchVMStatus])

    // Status stats
    const statusStats = useMemo(() => {
        const stats = { active: 0, waiting: 0, offline: 0, dial_level_0: 0 }
        bots.forEach(bot => {
            const s = bot.effective_status || 'offline'
            if (s === 'active' || s === 'running') stats.active++
            else if (s === 'dial_level_0') stats.dial_level_0++
            else if (s === 'waiting') stats.waiting++
            else stats.offline++
        })
        return stats
    }, [bots])

    // Totals
    const totals = useMemo(() => bots.reduce((acc, b) => ({
        calls: acc.calls + (b.calls || 0),
        xfer: acc.xfer + (b.xfer || 0),
        am: acc.am + (b.am || 0),
        ni: acc.ni + (b.ni || 0),
        na: acc.na + (b.na || 0),
        dnc: acc.dnc + (b.dnc || 0),
        dnq: acc.dnq + (b.dnq || 0)
    }), { calls: 0, xfer: 0, am: 0, ni: 0, na: 0, dnc: 0, dnq: 0 }), [bots])

    // Filter & paginate
    const filteredBots = useMemo(() => {
        if (!selectedStatus) return bots
        return bots.filter(b => {
            const s = b.effective_status || 'offline'
            return selectedStatus === 'active' ? (s === 'active' || s === 'running') : s === selectedStatus
        })
    }, [bots, selectedStatus])

    const totalPages = Math.ceil(filteredBots.length / pageSize)
    const paginatedBots = filteredBots.slice((currentPage - 1) * pageSize, currentPage * pageSize)

    useEffect(() => { setCurrentPage(1) }, [selectedStatus, datePreset])

    // Execute bot command (internal - after confirmation)
    const executeCommand = async (sipIds, command) => {
        const progressMsgs = command === 'start' ? START_PROGRESS : STOP_PROGRESS
        setCommandState(prev => {
            const next = { ...prev }
            sipIds.forEach(sipId => {
                next[sipId] = { cmd: command, progress: 0, status: progressMsgs[0] }
            })
            return next
        })
        try {
            await monitoringApi.sendCommand(sipIds, command, dialerId)
        } catch (err) {
            console.error(`Command failed:`, err)
            showToast('Command failed: ' + (err.response?.data?.error || err.message), 'error')
            setCommandState(prev => {
                const n = { ...prev }
                sipIds.forEach(sipId => delete n[sipId])
                return n
            })
        }
    }

    // Bot commands with confirmation dialogs for auto-login/auto-revive
    const sendCommand = async (sipId, command) => {
        // Find the bot to check its status
        const bot = bots.find(b => b.sip_id === sipId)

        // For start command
        if (command === 'start' && bot) {
            const lastHB = bot.last_heartbeat ? new Date(bot.last_heartbeat) : null
            const hbAge = lastHB ? (Date.now() - lastHB) / 1000 : Infinity

            // Warn if bot appears offline (no recent heartbeat)
            if (hbAge > 120) {
                setCommandState(prev => ({
                    ...prev,
                    [sipId]: { cmd: command, progress: 2, status: 'Bot offline - start VM first', error: true, isOfflineWarning: true }
                }))
                showToast(`Bot ${sipId} appears offline. Start the VM first.`, 'error')
                return
            }

            // Check if bot doesn't have auto_login enabled - offer to enable it
            if (!bot.auto_login) {
                setConfirmAction({
                    title: 'Start Bot?',
                    message: `Bot ${sipId} does not have auto-login enabled. Would you like to enable it so the bot automatically logs in after VM restart?`,
                    checkboxes: [
                        { label: 'Enable Auto-Login', checked: true },
                        { label: 'Enable Auto-Revive (restart on crash)', checked: false }
                    ],
                    confirmText: 'Start Bot',
                    onConfirm: async (checkboxStates) => {
                        setConfirmAction(null)
                        try {
                            if (checkboxStates[0]) {
                                await monitoringApi.setAutoLogin(sipId, true, dialerId)
                                setBots(prev => prev.map(b => b.sip_id === sipId ? { ...b, auto_login: 1 } : b))
                            }
                            if (checkboxStates[1]) {
                                await monitoringApi.setAutoRevive(sipId, true, dialerId)
                                setBots(prev => prev.map(b => b.sip_id === sipId ? { ...b, auto_revive: 1 } : b))
                            }
                            if (checkboxStates[0] || checkboxStates[1]) {
                                showToast('Auto features enabled', 'success')
                            }
                        } catch (err) {
                            showToast('Failed to enable auto features: ' + err.message, 'error')
                        }
                        executeCommand([sipId], command)
                    }
                })
                return
            }
        }

        // For stop command, check if bot has auto_login or auto_revive enabled
        if (command === 'stop' && bot && (bot.auto_login || bot.auto_revive)) {
            setConfirmAction({
                title: 'Stop Bot?',
                message: `Bot ${sipId} has auto-login${bot.auto_revive ? ' and auto-revive' : ''} enabled. Also disable auto features?`,
                checkboxes: [
                    { label: 'Disable Auto-Login and Auto-Revive before stopping', checked: false }
                ],
                confirmText: 'Stop Bot',
                onConfirm: async (checkboxStates) => {
                    setConfirmAction(null)
                    if (checkboxStates[0]) {
                        try {
                            if (bot.auto_revive) {
                                await monitoringApi.setAutoRevive(sipId, false, dialerId)
                                setBots(prev => prev.map(b => b.sip_id === sipId ? { ...b, auto_revive: 0 } : b))
                            }
                            if (bot.auto_login) {
                                await monitoringApi.setAutoLogin(sipId, false, dialerId)
                                setBots(prev => prev.map(b => b.sip_id === sipId ? { ...b, auto_login: 0 } : b))
                            }
                            showToast('Auto-login/revive disabled', 'success')
                        } catch (err) {
                            showToast('Failed to disable auto features: ' + err.message, 'error')
                        }
                    }
                    executeCommand([sipId], command)
                }
            })
            return
        }

        // For other commands or bots without relevant auto features, execute directly
        executeCommand([sipId], command)
    }

    // VM Control functions with confirmation
    const executeVMAction = async (action, sipId, serverIP, vmid, options = {}) => {
        setExpandedBot(null)
        setCommandState(prev => ({ ...prev, [sipId]: { cmd: `vm_${action}`, progress: 0, status: `${action === 'start' ? 'Starting' : action === 'stop' ? 'Stopping' : 'Restarting'} VM...` } }))

        try {
            if (action === 'start') {
                await monitoringApi.vmStart(serverIP, vmid, sipId, dialerId)
                showToast(`VM ${vmid} start command sent`, 'success')
            } else if (action === 'stop') {
                await monitoringApi.vmStop(serverIP, vmid, sipId, dialerId)
                showToast(`VM ${vmid} stop command sent`, 'success')
            } else if (action === 'restart') {
                await monitoringApi.vmRestart(serverIP, vmid, sipId, false, dialerId) // Never auto-signin, use auto_login flag instead
                showToast(`VM ${vmid} restart command sent`, 'success')
            }
            setTimeout(() => {
                setCommandState(prev => { const n = { ...prev }; delete n[sipId]; return n })
                // Refresh VM status
                const bot = bots.find(b => b.sip_id === sipId)
                if (bot) fetchVMStatus(bot)
            }, 5000)
        } catch (err) {
            showToast(`Failed to ${action} VM: ` + (err.response?.data?.error || err.message), 'error')
            setCommandState(prev => { const n = { ...prev }; delete n[sipId]; return n })
        }
    }

    // VM actions with confirmation dialog and auto-toggle options
    const vmAction = (action, bot) => {
        const actionLabels = { start: 'Start', stop: 'Stop', restart: 'Restart' }

        // For stop/restart - offer to disable auto features
        if ((action === 'stop' || action === 'restart') && (bot.auto_login || bot.auto_revive)) {
            setConfirmAction({
                title: `${actionLabels[action]} VM?`,
                message: `Are you sure you want to ${action} VM ${bot.vmid}? Also disable auto-login/revive?`,
                checkboxes: [
                    { label: 'Disable Auto-Login and Auto-Revive', checked: false }
                ],
                confirmText: `${actionLabels[action]} VM`,
                onConfirm: async (checkboxStates) => {
                    setConfirmAction(null)
                    if (checkboxStates[0]) {
                        try {
                            if (bot.auto_revive) await monitoringApi.setAutoRevive(bot.sip_id, false, dialerId)
                            if (bot.auto_login) await monitoringApi.setAutoLogin(bot.sip_id, false, dialerId)
                            setBots(prev => prev.map(b =>
                                b.sip_id === bot.sip_id ? { ...b, auto_login: 0, auto_revive: 0 } : b
                            ))
                            showToast('Auto-login/revive disabled', 'success')
                        } catch (err) {
                            showToast('Failed to disable auto features: ' + err.message, 'error')
                        }
                    }
                    executeVMAction(action, bot.sip_id, bot.server_ip, bot.vmid, {})
                }
            })
            return
        }

        // For start - offer to enable auto features
        if (action === 'start' && !bot.auto_login) {
            setConfirmAction({
                title: `${actionLabels[action]} VM?`,
                message: `Are you sure you want to start VM ${bot.vmid}? Bot doesn't have auto-login enabled.`,
                checkboxes: [
                    { label: 'Enable Auto-Login', checked: true },
                    { label: 'Enable Auto-Revive (restart on crash)', checked: false }
                ],
                confirmText: 'Start VM',
                onConfirm: async (checkboxStates) => {
                    setConfirmAction(null)
                    try {
                        if (checkboxStates[0]) {
                            await monitoringApi.setAutoLogin(bot.sip_id, true, dialerId)
                            setBots(prev => prev.map(b =>
                                b.sip_id === bot.sip_id ? { ...b, auto_login: 1 } : b
                            ))
                        }
                        if (checkboxStates[1]) {
                            await monitoringApi.setAutoRevive(bot.sip_id, true, dialerId)
                            setBots(prev => prev.map(b =>
                                b.sip_id === bot.sip_id ? { ...b, auto_revive: 1 } : b
                            ))
                        }
                        if (checkboxStates[0] || checkboxStates[1]) {
                            showToast('Auto features enabled', 'success')
                        }
                    } catch (err) {
                        showToast('Failed to enable auto features: ' + err.message, 'error')
                    }
                    executeVMAction(action, bot.sip_id, bot.server_ip, bot.vmid, {})
                }
            })
            return
        }

        // Default confirmation without checkboxes
        setConfirmAction({
            title: `${actionLabels[action]} VM?`,
            message: `Are you sure you want to ${action} VM ${bot.vmid}?`,
            confirmText: `${actionLabels[action]} VM`,
            onConfirm: () => {
                setConfirmAction(null)
                executeVMAction(action, bot.sip_id, bot.server_ip, bot.vmid, {})
            }
        })
    }

    // Toggle auto-login for a bot
    const toggleAutoLogin = async (bot) => {
        const newValue = !bot.auto_login
        // Optimistically update UI
        setBots(prev => prev.map(b =>
            b.sip_id === bot.sip_id ? { ...b, auto_login: newValue ? 1 : 0 } : b
        ))
        try {
            await monitoringApi.setAutoLogin(bot.sip_id, newValue, dialerId)
            showToast(`Auto-login ${newValue ? 'enabled' : 'disabled'} for ${bot.sip_id}`, 'success')
        } catch (err) {
            // Revert on error
            setBots(prev => prev.map(b =>
                b.sip_id === bot.sip_id ? { ...b, auto_login: bot.auto_login } : b
            ))
            showToast('Failed to update auto-login: ' + (err.response?.data?.error || err.message), 'error')
        }
    }

    // Toggle auto-revive for a bot (only works if auto_login is enabled)
    const toggleAutoRevive = async (bot) => {
        if (!bot.auto_login) {
            showToast('Enable Auto-Login first before enabling Auto-Revive', 'error')
            return
        }
        const newValue = !bot.auto_revive
        // Optimistically update UI
        setBots(prev => prev.map(b =>
            b.sip_id === bot.sip_id ? { ...b, auto_revive: newValue ? 1 : 0, auto_revive_count: 0 } : b
        ))
        try {
            await monitoringApi.setAutoRevive(bot.sip_id, newValue, dialerId)
            showToast(`Auto-revive ${newValue ? 'enabled' : 'disabled'} for ${bot.sip_id}`, 'success')
        } catch (err) {
            // Revert on error
            setBots(prev => prev.map(b =>
                b.sip_id === bot.sip_id ? { ...b, auto_revive: bot.auto_revive } : b
            ))
            showToast('Failed to update auto-revive: ' + (err.response?.data?.error || err.message), 'error')
        }
    }

    // Bulk toggle auto-login for selected bots
    const bulkToggleAutoLogin = async (enabled) => {
        if (selectedBots.size === 0) return
        setBulkLoading(true)
        const sipIds = [...selectedBots]

        // Optimistically update UI
        setBots(prev => prev.map(b =>
            sipIds.includes(b.sip_id) ? { ...b, auto_login: enabled ? 1 : 0 } : b
        ))

        try {
            // Call API for each selected bot
            await Promise.all(sipIds.map(sipId => monitoringApi.setAutoLogin(sipId, enabled, dialerId)))
            showToast(`Auto-login ${enabled ? 'enabled' : 'disabled'} for ${sipIds.length} bots`, 'success')
            setSelectedBots(new Set())
        } catch (err) {
            // Revert on error
            fetchBots()
            showToast('Failed to update auto-login: ' + (err.response?.data?.error || err.message), 'error')
        } finally {
            setBulkLoading(false)
        }
    }

    // Bulk toggle auto-revive for selected bots (requires auto_login to be enabled)
    const bulkToggleAutoRevive = async (enabled) => {
        if (selectedBots.size === 0) return
        setBulkLoading(true)
        const sipIds = [...selectedBots]

        // Filter out bots without auto_login if enabling
        const eligibleBots = enabled
            ? bots.filter(b => sipIds.includes(b.sip_id) && b.auto_login)
            : bots.filter(b => sipIds.includes(b.sip_id))
        const eligibleIds = eligibleBots.map(b => b.sip_id)

        if (enabled && eligibleIds.length < sipIds.length) {
            showToast(`${sipIds.length - eligibleIds.length} bot(s) skipped - enable Auto-Login first`, 'error')
        }

        if (eligibleIds.length === 0) {
            setBulkLoading(false)
            return
        }

        // Optimistically update UI
        setBots(prev => prev.map(b =>
            eligibleIds.includes(b.sip_id) ? { ...b, auto_revive: enabled ? 1 : 0, auto_revive_count: 0 } : b
        ))

        try {
            await Promise.all(eligibleIds.map(sipId => monitoringApi.setAutoRevive(sipId, enabled, dialerId)))
            showToast(`Auto-revive ${enabled ? 'enabled' : 'disabled'} for ${eligibleIds.length} bots`, 'success')
            setSelectedBots(new Set())
        } catch (err) {
            fetchBots()
            showToast('Failed to update auto-revive: ' + (err.response?.data?.error || err.message), 'error')
        } finally {
            setBulkLoading(false)
        }
    }

    // Execute bulk VM action (internal - after confirmation)
    const executeBulkVMAction = async (botsWithVM, action) => {
        setBulkLoading(true)

        const byServer = {}
        botsWithVM.forEach(bot => {
            if (!byServer[bot.server_ip]) byServer[bot.server_ip] = []
            byServer[bot.server_ip].push(bot)
        })

        let successCount = 0
        let failCount = 0

        try {
            for (const [serverIP, serverBots] of Object.entries(byServer)) {
                const vmids = serverBots.map(b => b.vmid)
                try {
                    await monitoringApi.bulkVMAction(serverIP, vmids, action)
                    successCount += vmids.length
                } catch (err) {
                    console.error(`Failed to ${action} VMs on ${serverIP}:`, err)
                    failCount += vmids.length
                }
            }

            const actionLabel = action === 'start' ? 'Started' : action === 'stop' ? 'Stopped' : 'Restarted'
            if (successCount > 0) {
                showToast(`${actionLabel} ${successCount} VM${successCount > 1 ? 's' : ''}${failCount > 0 ? ` (${failCount} failed)` : ''}`, 'success')
            } else {
                showToast(`Failed to ${action} VMs`, 'error')
            }
            setSelectedBots(new Set())
        } catch (err) {
            showToast(`Failed to ${action} VMs: ` + (err.response?.data?.error || err.message), 'error')
        } finally {
            setBulkLoading(false)
        }
    }

    // Bulk start VMs for selected bots
    const bulkStartVMs = () => {
        if (selectedBots.size === 0) return

        const botsWithVM = bots.filter(b => selectedBots.has(b.sip_id) && b.server_ip && b.vmid)

        if (botsWithVM.length === 0) {
            showToast('No VMs linked to selected bots', 'error')
            return
        }

        // Check for bots without auto_login
        const botsWithoutAuto = botsWithVM.filter(b => !b.auto_login)

        if (botsWithoutAuto.length > 0) {
            setConfirmAction({
                title: `Start ${botsWithVM.length} VM${botsWithVM.length > 1 ? 's' : ''}?`,
                message: `${botsWithoutAuto.length} of ${botsWithVM.length} bot(s) don't have auto-login enabled.`,
                checkboxes: [
                    { label: `Enable Auto-Login for ${botsWithoutAuto.length} bot(s)`, checked: true },
                    { label: `Enable Auto-Revive for ${botsWithoutAuto.length} bot(s)`, checked: false }
                ],
                confirmText: 'Start VMs',
                onConfirm: async (checkboxStates) => {
                    setConfirmAction(null)
                    try {
                        for (const bot of botsWithoutAuto) {
                            if (checkboxStates[0]) await monitoringApi.setAutoLogin(bot.sip_id, true, dialerId)
                            if (checkboxStates[1]) await monitoringApi.setAutoRevive(bot.sip_id, true, dialerId)
                        }
                        if (checkboxStates[0] || checkboxStates[1]) {
                            setBots(prev => prev.map(b =>
                                botsWithoutAuto.some(ab => ab.sip_id === b.sip_id)
                                    ? {
                                        ...b,
                                        auto_login: checkboxStates[0] ? 1 : b.auto_login,
                                        auto_revive: checkboxStates[1] ? 1 : b.auto_revive
                                    }
                                    : b
                            ))
                            showToast('Auto features enabled', 'success')
                        }
                    } catch (err) {
                        showToast('Failed to enable auto features: ' + err.message, 'error')
                    }
                    executeBulkVMAction(botsWithVM, 'start')
                }
            })
            return
        }

        // No confirmation needed
        executeBulkVMAction(botsWithVM, 'start')
    }

    // Bulk restart VMs
    const bulkRestartVMs = () => {
        if (selectedBots.size === 0) return

        const botsWithVM = bots.filter(b => selectedBots.has(b.sip_id) && b.server_ip && b.vmid)

        if (botsWithVM.length === 0) {
            showToast('No VMs linked to selected bots', 'error')
            return
        }

        // Check for bots with auto features
        const botsWithAuto = botsWithVM.filter(b => b.auto_login || b.auto_revive)

        setConfirmAction({
            title: `Restart ${botsWithVM.length} VM${botsWithVM.length > 1 ? 's' : ''}?`,
            message: `This will interrupt any active calls.${botsWithAuto.length > 0 ? ` ${botsWithAuto.length} bot(s) have auto-login enabled and will auto-restart.` : ''}`,
            checkboxes: botsWithAuto.length > 0 ? [
                { label: `Disable Auto-Login/Revive for ${botsWithAuto.length} bot(s)`, checked: false }
            ] : [],
            confirmText: 'Restart VMs',
            onConfirm: async (checkboxStates) => {
                setConfirmAction(null)
                if (botsWithAuto.length > 0 && checkboxStates[0]) {
                    try {
                        for (const bot of botsWithAuto) {
                            if (bot.auto_revive) await monitoringApi.setAutoRevive(bot.sip_id, false, dialerId)
                            if (bot.auto_login) await monitoringApi.setAutoLogin(bot.sip_id, false, dialerId)
                        }
                        setBots(prev => prev.map(b =>
                            botsWithAuto.some(ab => ab.sip_id === b.sip_id)
                                ? { ...b, auto_login: 0, auto_revive: 0 }
                                : b
                        ))
                        showToast('Auto features disabled', 'success')
                    } catch (err) {
                        showToast('Failed to disable auto features: ' + err.message, 'error')
                    }
                }
                executeBulkVMAction(botsWithVM, 'restart')
            }
        })
    }

    // Bulk stop VMs
    const bulkStopVMs = () => {
        if (selectedBots.size === 0) return

        const botsWithVM = bots.filter(b => selectedBots.has(b.sip_id) && b.server_ip && b.vmid)

        if (botsWithVM.length === 0) {
            showToast('No VMs linked to selected bots', 'error')
            return
        }

        // Check for bots with auto features
        const botsWithAuto = botsWithVM.filter(b => b.auto_login || b.auto_revive)

        setConfirmAction({
            title: `Stop ${botsWithVM.length} VM${botsWithVM.length > 1 ? 's' : ''}?`,
            message: `This will terminate any active calls.${botsWithAuto.length > 0 ? ` Also disable auto-login/revive?` : ''}`,
            checkboxes: botsWithAuto.length > 0 ? [
                { label: `Disable Auto-Login/Revive for ${botsWithAuto.length} bot(s)`, checked: false }
            ] : [],
            confirmText: 'Stop VMs',
            onConfirm: async (checkboxStates) => {
                setConfirmAction(null)
                if (botsWithAuto.length > 0 && checkboxStates[0]) {
                    try {
                        for (const bot of botsWithAuto) {
                            if (bot.auto_revive) await monitoringApi.setAutoRevive(bot.sip_id, false, dialerId)
                            if (bot.auto_login) await monitoringApi.setAutoLogin(bot.sip_id, false, dialerId)
                        }
                        setBots(prev => prev.map(b =>
                            botsWithAuto.some(ab => ab.sip_id === b.sip_id)
                                ? { ...b, auto_login: 0, auto_revive: 0 }
                                : b
                        ))
                        showToast('Auto features disabled', 'success')
                    } catch (err) {
                        showToast('Failed to disable auto features: ' + err.message, 'error')
                    }
                }
                executeBulkVMAction(botsWithVM, 'stop')
            }
        })
    }

    // Bulk operations
    const toggleSelectAll = () => {
        if (selectedBots.size === filteredBots.length) {
            setSelectedBots(new Set())
        } else {
            setSelectedBots(new Set(filteredBots.map(b => b.sip_id)))
        }
    }

    const toggleSelect = (sipId) => {
        setSelectedBots(prev => {
            const next = new Set(prev)
            if (next.has(sipId)) next.delete(sipId)
            else next.add(sipId)
            return next
        })
    }

    // Execute bulk command (internal - after confirmation)
    const executeBulkCommand = async (sipIds, command) => {
        setBulkLoading(true)
        const progressMsgs = command === 'start' ? START_PROGRESS : STOP_PROGRESS

        if (command === 'start') {
            const onlineBots = []
            const offlineBots = []

            sipIds.forEach(sipId => {
                const bot = bots.find(b => b.sip_id === sipId)
                if (bot) {
                    const lastHB = bot.last_heartbeat ? new Date(bot.last_heartbeat) : null
                    const hbAge = lastHB ? (Date.now() - lastHB) / 1000 : Infinity
                    if (hbAge > 120) {
                        offlineBots.push(sipId)
                    } else {
                        onlineBots.push(sipId)
                    }
                }
            })

            if (offlineBots.length > 0) {
                setCommandState(prev => {
                    const next = { ...prev }
                    offlineBots.forEach(sipId => {
                        next[sipId] = { cmd: command, progress: 2, status: 'VM offline - start VM first', error: true }
                    })
                    return next
                })
                showToast(`${offlineBots.length} bot(s) appear offline - start VMs first`, 'error')
            }

            if (onlineBots.length > 0) {
                try {
                    await monitoringApi.sendCommand(onlineBots, command, dialerId)
                    setCommandState(prev => {
                        const next = { ...prev }
                        onlineBots.forEach(sipId => {
                            next[sipId] = { cmd: command, progress: 0, status: progressMsgs[0] }
                        })
                        return next
                    })
                    showToast(`${command === 'stop' ? 'Logout' : 'Login'} command sent to ${onlineBots.length} bots`, 'success')
                    // Clear "Sending..." status after 15 seconds - actual status will show from bot refresh
                    setTimeout(() => {
                        setCommandState(prev => {
                            const next = { ...prev }
                            onlineBots.forEach(sipId => {
                                if (next[sipId]?.status === progressMsgs[0]) {
                                    delete next[sipId]
                                }
                            })
                            return next
                        })
                    }, 15000)
                } catch (err) {
                    showToast('Start command failed', 'error')
                }
            }
        } else {
            try {
                await monitoringApi.sendCommand(sipIds, command, dialerId)
                setCommandState(prev => {
                    const next = { ...prev }
                    sipIds.forEach(sipId => {
                        next[sipId] = { cmd: command, progress: 0, status: progressMsgs[0] }
                    })
                    return next
                })
                showToast(`${command === 'stop' ? 'Logout' : 'Login'} command sent to ${sipIds.length} bots`, 'success')
                // Clear "Sending..." status after 15 seconds - actual status will show from bot refresh
                setTimeout(() => {
                    setCommandState(prev => {
                        const next = { ...prev }
                        sipIds.forEach(sipId => {
                            if (next[sipId]?.status === progressMsgs[0]) {
                                delete next[sipId]
                            }
                        })
                        return next
                    })
                }, 15000)
            } catch (err) {
                showToast('Bulk command failed', 'error')
            }
        }

        setSelectedBots(new Set())
        setBulkLoading(false)
    }

    const bulkCommand = (command) => {
        if (selectedBots.size === 0) return

        const sipIds = [...selectedBots]
        const selectedBotsData = bots.filter(b => sipIds.includes(b.sip_id))

        // For stop command - check for bots with auto_login/auto_revive
        if (command === 'stop') {
            const botsWithAuto = selectedBotsData.filter(b => b.auto_login || b.auto_revive)
            if (botsWithAuto.length > 0) {
                setConfirmAction({
                    title: `Stop ${sipIds.length} Bot${sipIds.length > 1 ? 's' : ''}?`,
                    message: `${botsWithAuto.length} of ${sipIds.length} selected bot(s) have auto-login/revive enabled. Also disable?`,
                    checkboxes: [
                        { label: `Disable Auto-Login and Auto-Revive for ${botsWithAuto.length} bot(s)`, checked: false }
                    ],
                    confirmText: 'Stop Bots',
                    onConfirm: async (checkboxStates) => {
                        setConfirmAction(null)
                        if (checkboxStates[0]) {
                            try {
                                for (const bot of botsWithAuto) {
                                    if (bot.auto_revive) await monitoringApi.setAutoRevive(bot.sip_id, false, dialerId)
                                    if (bot.auto_login) await monitoringApi.setAutoLogin(bot.sip_id, false, dialerId)
                                }
                                setBots(prev => prev.map(b =>
                                    botsWithAuto.some(ab => ab.sip_id === b.sip_id)
                                        ? { ...b, auto_login: 0, auto_revive: 0 }
                                        : b
                                ))
                                showToast(`Auto features disabled for ${botsWithAuto.length} bot(s)`, 'success')
                            } catch (err) {
                                showToast('Failed to disable auto features: ' + err.message, 'error')
                            }
                        }
                        executeBulkCommand(sipIds, command)
                    }
                })
                return
            }
        }

        // For start command - check for bots without auto_login
        if (command === 'start') {
            const botsWithoutAuto = selectedBotsData.filter(b => !b.auto_login)
            if (botsWithoutAuto.length > 0) {
                setConfirmAction({
                    title: `Start ${sipIds.length} Bot${sipIds.length > 1 ? 's' : ''}?`,
                    message: `${botsWithoutAuto.length} of ${sipIds.length} selected bot(s) don't have auto-login enabled. Would you like to enable it?`,
                    checkboxes: [
                        { label: `Enable Auto-Login for ${botsWithoutAuto.length} bot(s)`, checked: true },
                        { label: `Enable Auto-Revive for ${botsWithoutAuto.length} bot(s)`, checked: false }
                    ],
                    confirmText: 'Start Bots',
                    onConfirm: async (checkboxStates) => {
                        setConfirmAction(null)
                        try {
                            for (const bot of botsWithoutAuto) {
                                if (checkboxStates[0]) await monitoringApi.setAutoLogin(bot.sip_id, true, dialerId)
                                if (checkboxStates[1]) await monitoringApi.setAutoRevive(bot.sip_id, true, dialerId)
                            }
                            if (checkboxStates[0] || checkboxStates[1]) {
                                setBots(prev => prev.map(b =>
                                    botsWithoutAuto.some(ab => ab.sip_id === b.sip_id)
                                        ? {
                                            ...b,
                                            auto_login: checkboxStates[0] ? 1 : b.auto_login,
                                            auto_revive: checkboxStates[1] ? 1 : b.auto_revive
                                        }
                                        : b
                                ))
                                showToast('Auto features enabled', 'success')
                            }
                        } catch (err) {
                            showToast('Failed to enable auto features: ' + err.message, 'error')
                        }
                        executeBulkCommand(sipIds, command)
                    }
                })
                return
            }
        }

        // No confirmation needed - execute directly
        executeBulkCommand(sipIds, command)
    }

    const getStatusConfig = (s) => STATUS_CONFIG[s] || STATUS_CONFIG.offline

    return (
        <div className="bots-tab-modern">
            {/* Toast Container */}
            <div className="bt-toast-container">
                {toasts.map(t => (
                    <Toast key={t.id} message={t.message} type={t.type} onClose={() => removeToast(t.id)} />
                ))}
            </div>

            {/* Confirmation Dialog */}
            {confirmAction && (
                <ConfirmDialog
                    title={confirmAction.title}
                    message={confirmAction.message}
                    checkboxes={confirmAction.checkboxes || []}
                    confirmText={confirmAction.confirmText}
                    onConfirm={confirmAction.onConfirm}
                    onCancel={() => setConfirmAction(null)}
                />
            )}

            {/* Header */}
            <div className="bt-header">
                <div className="bt-header-left">
                    <h2><Bot size={18} /> Bots</h2>
                    <span className="bt-count">{bots.length}</span>
                </div>

                {/* Date Selector */}
                <div className="bt-date-selector">
                    {DATE_PRESETS.map(p => (
                        <button
                            key={p.key}
                            className={`bt-date-btn ${datePreset === p.key ? 'active' : ''}`}
                            onClick={() => {
                                setDatePreset(p.key)
                                if (p.key === 'custom') setShowDatePicker(true)
                                else setShowDatePicker(false)
                            }}
                        >
                            {p.label}
                        </button>
                    ))}
                    {showDatePicker && datePreset === 'custom' && (
                        <div className="bt-date-picker">
                            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                            <span>to</span>
                            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                            <button className="bt-date-apply" onClick={() => { setShowDatePicker(false); fetchBots(); }}>Apply</button>
                        </div>
                    )}
                </div>

                <button className="bt-refresh-btn" onClick={fetchBots} disabled={loading}>
                    <RefreshCw size={14} className={loading ? 'spinning' : ''} />
                </button>
            </div>

            {/* Servers Section - Row Format */}
            {servers.length > 0 && (
                <div className="bt-servers-section">
                    <button
                        className="bt-servers-header"
                        onClick={() => setServersCollapsed(!serversCollapsed)}
                    >
                        {serversCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        <Server size={16} />
                        <span>Servers ({servers.length} hosting bots for this dialer)</span>
                        {serversLoading && <RefreshCw size={12} className="spinning" />}
                    </button>

                    {!serversCollapsed && (
                        <div className="bt-servers-rows">
                            {servers.map(server => {
                                const cpuStatus = server.cpu_usage >= 90 ? 'critical' : server.cpu_usage >= 70 ? 'warning' : 'ok'
                                const ramPercent = server.ram_total_gb > 0 ? (server.ram_used_gb / server.ram_total_gb) * 100 : 0
                                const ramStatus = ramPercent >= 95 ? 'critical' : ramPercent >= 80 ? 'warning' : 'ok'

                                return (
                                    <div key={server.ip} className="bt-server-row">
                                        <div className="bt-server-row-info">
                                            <span className="bt-server-row-ip">{server.ip}</span>
                                            {server.name && <span className="bt-server-row-name">{server.name}</span>}
                                        </div>

                                        <div className="bt-server-row-stats">
                                            <span className={`bt-server-row-stat bt-server-stat-${cpuStatus}`}>
                                                <Cpu size={12} />
                                                {(server.cpu_usage || 0).toFixed(0)}%
                                            </span>
                                            <span className={`bt-server-row-stat bt-server-stat-${ramStatus}`}>
                                                <HardDrive size={12} />
                                                {ramPercent.toFixed(0)}%
                                            </span>
                                        </div>

                                        <div className="bt-server-row-vms">
                                            <span className="bt-server-row-vms-dialer">
                                                <strong>{server.vms_for_dialer || 0}</strong> VMs
                                            </span>
                                            <span className="bt-server-row-vms-total">
                                                {server.vm_running || 0}/{server.vm_count || 0} total
                                            </span>
                                        </div>

                                        <div className="bt-server-row-online">
                                            <span className="bt-row-online">
                                                <Activity size={11} /> {server.online_for_dialer || 0}
                                            </span>
                                            <span className="bt-row-offline">
                                                <WifiOff size={11} /> {server.offline_for_dialer || 0}
                                            </span>
                                        </div>

                                        <button
                                            className="bt-server-row-link"
                                            onClick={() => navigate(`/servers?ip=${server.ip}`)}
                                        >
                                            <ExternalLink size={12} />
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Stats Row */}
            <div className="bt-stats-row">
                <div className="bt-status-cards">
                    {[
                        { key: 'active', count: statusStats.active, icon: Activity, color: '#10b981', label: 'active' },
                        { key: 'dial_level_0', count: statusStats.dial_level_0, icon: AlertTriangle, color: '#ef4444', label: 'dial lvl 0' },
                        { key: 'waiting', count: statusStats.waiting, icon: Clock, color: '#f59e0b', label: 'logged out' },
                        { key: 'offline', count: statusStats.offline, icon: WifiOff, color: '#6b7280', label: 'offline' }
                    ].map(s => (
                        <button
                            key={s.key}
                            className={`bt-stat-card bt-stat-${s.key} ${selectedStatus === s.key ? 'active' : ''}`}
                            onClick={() => setSelectedStatus(selectedStatus === s.key ? '' : s.key)}
                        >
                            <s.icon size={14} />
                            <span className="bt-stat-num">{s.count}</span>
                            <span className="bt-stat-label">{s.label}</span>
                        </button>
                    ))}
                </div>

                <div className="bt-dispo-cards">
                    <div className="bt-dispo-card" title="Total Calls"><Phone size={12} /><span>{totals.calls}</span></div>
                    <div className="bt-dispo-card bt-dispo-xfer" title="Transfers"><PhoneForwarded size={12} /><span>{totals.xfer}</span></div>
                    <div className="bt-dispo-card bt-dispo-am" title="AM"><Voicemail size={12} /><span>{totals.am}</span></div>
                    <div className="bt-dispo-card bt-dispo-ni" title="NI"><PhoneOff size={12} /><span>{totals.ni}</span></div>
                    <div className="bt-dispo-card bt-dispo-na" title="NA"><PhoneMissed size={12} /><span>{totals.na}</span></div>
                    <div className="bt-dispo-card bt-dispo-dnc" title="DNC"><Ban size={12} /><span>{totals.dnc}</span></div>
                    <div className="bt-dispo-card bt-dispo-dnq" title="DNQ"><UserX size={12} /><span>{totals.dnq}</span></div>
                </div>

                {/* Bulk Actions */}
                {selectedBots.size > 0 && (
                    <div className="bt-bulk-actions">
                        <span className="bt-bulk-count">{selectedBots.size} selected</span>
                        <button className="bt-bulk-btn bt-bulk-start" onClick={() => bulkCommand('start')} disabled={bulkLoading} title="Login bots to ViciDial">
                            <Play size={12} /> Login
                        </button>
                        <button className="bt-bulk-btn bt-bulk-stop" onClick={() => bulkCommand('stop')} disabled={bulkLoading} title="Logout bots from ViciDial">
                            <Square size={12} /> Logout
                        </button>
                        <span className="bt-bulk-divider" />
                        <button className="bt-bulk-btn bt-bulk-auto-on" onClick={() => bulkToggleAutoLogin(true)} disabled={bulkLoading} title="Enable Auto-Login">
                            <ToggleRight size={14} /> Login On
                        </button>
                        <button className="bt-bulk-btn bt-bulk-auto-off" onClick={() => bulkToggleAutoLogin(false)} disabled={bulkLoading} title="Disable Auto-Login">
                            <ToggleLeft size={14} /> Login Off
                        </button>
                        <button className="bt-bulk-btn bt-bulk-revive-on" onClick={() => bulkToggleAutoRevive(true)} disabled={bulkLoading} title="Enable Auto-Revive">
                            <RefreshCcw size={14} /> Revive On
                        </button>
                        <button className="bt-bulk-btn bt-bulk-revive-off" onClick={() => bulkToggleAutoRevive(false)} disabled={bulkLoading} title="Disable Auto-Revive">
                            <RefreshCcw size={14} /> Revive Off
                        </button>
                        <span className="bt-bulk-divider" />
                        <button className="bt-bulk-btn bt-bulk-vm-start" onClick={bulkStartVMs} disabled={bulkLoading} title="Start all selected VMs">
                            <Power size={12} /> Start VM
                        </button>
                        <button className="bt-bulk-btn bt-bulk-vm-restart" onClick={bulkRestartVMs} disabled={bulkLoading} title="Restart all selected VMs">
                            <RotateCcw size={12} /> Restart VM
                        </button>
                        <button className="bt-bulk-btn bt-bulk-vm-stop" onClick={bulkStopVMs} disabled={bulkLoading} title="Stop all selected VMs">
                            <PowerOff size={12} /> Stop VM
                        </button>
                        <button
                            className="bt-bulk-btn bt-bulk-clone"
                            onClick={() => {
                                // Get first selected bot with VM and navigate to servers page
                                const botsWithVM = bots.filter(b => selectedBots.has(b.sip_id) && b.server_ip && b.vmid)
                                if (botsWithVM.length > 0) {
                                    const firstBot = botsWithVM[0]
                                    navigate(`/servers?ip=${firstBot.server_ip}&bulkClone=true`)
                                } else {
                                    showToast('No VMs linked to selected bots', 'error')
                                }
                            }}
                            disabled={bulkLoading}
                            title="Bulk clone VMs"
                        >
                            <Copy size={12} /> Clone VM
                        </button>
                        <span className="bt-bulk-divider" />
                        <button
                            className="bt-bulk-btn bt-bulk-voice"
                            onClick={openBulkVoiceSetModal}
                            disabled={bulkLoading}
                            title="Edit voice sets for selected bots"
                        >
                            <Mic size={12} /> Voice Sets
                        </button>
                        <button
                            className="bt-bulk-btn bt-bulk-transcriber"
                            onClick={openBulkTranscriberModal}
                            disabled={bulkLoading}
                            title="Change transcriber for selected bots"
                        >
                            <Radio size={12} /> Transcriber
                        </button>
                        <button className="bt-bulk-btn bt-bulk-clear" onClick={() => setSelectedBots(new Set())}>Clear</button>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="bt-table-wrapper">
                {loading && bots.length === 0 ? (
                    <div className="bt-loading"><div className="bt-spinner" /><span>Loading...</span></div>
                ) : filteredBots.length === 0 ? (
                    <div className="bt-empty"><Bot size={32} /><p>No bots found</p></div>
                ) : (
                    <table className="bt-table">
                        <thead>
                            <tr>
                                <th className="bt-th-check">
                                    <button className="bt-check-btn" onClick={toggleSelectAll}>
                                        {selectedBots.size === filteredBots.length && filteredBots.length > 0
                                            ? <CheckSquare size={14} />
                                            : <SquareIcon size={14} />}
                                    </button>
                                </th>
                                <th className="bt-th-icon" title="Auto Login"><ToggleRight size={13} /></th>
                                <th className="bt-th-icon" title="Auto Revive"><RefreshCcw size={13} /></th>
                                <th>SIP</th>
                                <th>Status</th>
                                <th className="bt-th-icon" title="Total"><Phone size={11} /></th>
                                <th className="bt-th-icon bt-col-xfer" title="Xfer"><PhoneForwarded size={11} /></th>
                                <th className="bt-th-icon bt-col-am" title="AM"><Voicemail size={11} /></th>
                                <th className="bt-th-icon bt-col-ni" title="NI"><PhoneOff size={11} /></th>
                                <th className="bt-th-icon bt-col-na" title="NA"><PhoneMissed size={11} /></th>
                                <th className="bt-th-icon bt-col-dnc" title="DNC"><Ban size={11} /></th>
                                <th className="bt-th-icon bt-col-dnq" title="DNQ"><UserX size={11} /></th>
                                <th className="bt-th-icon" title="Call Status"><Phone size={11} /></th>
                                <th>Step</th>
                                <th className="bt-th-voice" title="Voice Sets"><Mic size={11} /></th>
                                <th className="bt-th-transcriber" title="Transcriber"><Radio size={11} /></th>
                                <th className="bt-th-icon" title="Git Branch: production=main, development=dev">Branch</th>
                                <th>HB</th>
                                <th className="bt-th-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedBots.map(bot => {
                                const status = bot.effective_status || 'offline'
                                const cfg = getStatusConfig(status)
                                const Icon = cfg.icon
                                const cmd = commandState[bot.sip_id]
                                const isProc = !!cmd
                                const isSelected = selectedBots.has(bot.sip_id)
                                const isExpanded = expandedBot === bot.sip_id
                                const currentVmStatus = vmStatus[bot.sip_id]

                                return (
                                    <>
                                        <tr key={bot.sip_id} className={`${status === 'active' ? 'row-active' : ''} ${isSelected ? 'row-selected' : ''} ${isExpanded ? 'row-expanded' : ''}`}>
                                            <td className="bt-td-check">
                                                <button className="bt-check-btn" onClick={() => toggleSelect(bot.sip_id)}>
                                                    {isSelected ? <CheckSquare size={14} /> : <SquareIcon size={14} />}
                                                </button>
                                            </td>
                                            <td className="bt-td-auto">
                                                <button
                                                    className={`bt-auto-btn ${bot.auto_login ? 'bt-auto-on' : ''}`}
                                                    onClick={() => toggleAutoLogin(bot)}
                                                    title={bot.auto_login ? 'Auto-login enabled - click to disable' : 'Auto-login disabled - click to enable'}
                                                >
                                                    {bot.auto_login ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                                                </button>
                                            </td>
                                            <td className="bt-td-auto">
                                                <button
                                                    className={`bt-revive-btn ${bot.auto_revive ? 'bt-revive-on' : ''} ${!bot.auto_login ? 'bt-revive-disabled' : ''}`}
                                                    onClick={() => toggleAutoRevive(bot)}
                                                    title={!bot.auto_login ? 'Enable Auto-Login first' : bot.auto_revive ? `Auto-revive enabled (${bot.auto_revive_count || 0}/3 revives)` : 'Auto-revive disabled'}
                                                >
                                                    <RefreshCcw size={14} />
                                                </button>
                                            </td>
                                            <td className="bt-td-sip">{bot.sip_id}</td>
                                            <td>
                                                {isProc ? (
                                                    <div className={`bt-progress-status ${cmd.error ? 'bt-progress-error' : ''}`}>
                                                        {cmd.error ? (
                                                            <X size={12} className="bt-error-icon" />
                                                        ) : (
                                                            <span className="bt-progress-spinner" />
                                                        )}
                                                        <span className="bt-progress-text">{cmd.status}</span>
                                                        {cmd.error && (
                                                            <button
                                                                className="bt-dismiss-error"
                                                                onClick={() => setCommandState(prev => { const n = { ...prev }; delete n[bot.sip_id]; return n })}
                                                                title="Dismiss"
                                                            >
                                                                <X size={10} />
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="bt-status-badge" style={{ '--c': cfg.color }}>
                                                        <Icon size={10} />{cfg.label}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="bt-td-num">{bot.calls || 0}</td>
                                            <td className="bt-td-num bt-col-xfer">{bot.xfer || 0}</td>
                                            <td className="bt-td-num bt-col-am">{bot.am || 0}</td>
                                            <td className="bt-td-num bt-col-ni">{bot.ni || 0}</td>
                                            <td className="bt-td-num bt-col-na">{bot.na || 0}</td>
                                            <td className="bt-td-num bt-col-dnc">{bot.dnc || 0}</td>
                                            <td className="bt-td-num bt-col-dnq">{bot.dnq || 0}</td>
                                            <td className="bt-td-call-status">
                                                <span className={`bt-call-indicator ${bot.status === 'in_call' ? 'bt-in-call' : ''}`} title={bot.status === 'in_call' ? 'In Call' : 'Idle'}>
                                                    <Phone size={12} />
                                                </span>
                                            </td>
                                            <td className="bt-td-step">{bot.current_step || '-'}</td>
                                            <td className="bt-td-voice">
                                                <span className="bt-voice-gender bt-voice-male" title="Male Voice Sets">
                                                    <User size={10} /> {bot.voice_sets_male || 0}
                                                </span>
                                                <span className="bt-voice-gender bt-voice-female" title="Female Voice Sets">
                                                    <User size={10} /> {bot.voice_sets_female || 0}
                                                </span>
                                            </td>
                                            <td className="bt-td-transcriber">
                                                <span
                                                    className={`bt-transcriber-badge bt-transcriber-${(bot.transcriber_type || 'VOSK').toLowerCase().replace('_', '-')}`}
                                                    title={bot.transcriber_type || 'VOSK'}
                                                >
                                                    <Radio size={10} />
                                                    <span className="bt-transcriber-label">
                                                        {(bot.transcriber_type || 'VOSK').replace('VOSK_V15L', 'V15L').replace('WHISPER_', 'W')}
                                                    </span>
                                                </span>
                                            </td>
                                            {/* Bot Mode (Git Branch) */}
                                            <td className="bt-td-branch">
                                                <button
                                                    className={`bt-branch-btn ${bot.bot_mode === 'development' ? 'bt-branch-dev' : bot.bot_mode === 'test' ? 'bt-branch-test' : bot.bot_mode === 'linux' ? 'bt-branch-linux' : 'bt-branch-prod'}`}
                                                    onClick={async () => {
                                                        if (!bot.license_id) {
                                                            showToast('No license found for this bot', 'error')
                                                            return
                                                        }
                                                        const modeOrder = ['production', 'development', 'test', 'linux']
                                                        const currentIdx = modeOrder.indexOf(bot.bot_mode || 'production')
                                                        const newMode = modeOrder[(currentIdx + 1) % modeOrder.length]
                                                        try {
                                                            await machineApi.setBotMode(bot.license_id, newMode)
                                                            setBots(prev => prev.map(b =>
                                                                b.sip_id === bot.sip_id ? { ...b, bot_mode: newMode } : b
                                                            ))
                                                            const branchMap = { production: 'main', development: 'dev', test: 'test', linux: 'linux-bot' }
                                                            showToast(`Bot mode: ${newMode} (${branchMap[newMode]} branch). Restart VM to apply.`, 'success')
                                                        } catch (err) {
                                                            const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to update bot mode'
                                                            showToast(msg, 'error')
                                                        }
                                                    }}
                                                    title={`${bot.bot_mode === 'development' ? 'Development (dev branch)' : bot.bot_mode === 'test' ? 'Test (test branch)' : bot.bot_mode === 'linux' ? 'Linux (linux-bot branch)' : 'Production (main branch)'} - Click to cycle`}
                                                >
                                                    {bot.bot_mode === 'development' ? 'DEV' : bot.bot_mode === 'test' ? 'TEST' : bot.bot_mode === 'linux' ? 'LNX' : 'PROD'}
                                                </button>
                                            </td>
                                            <td className="bt-td-hb">{formatTimeAgo(bot.last_heartbeat)}</td>
                                            <td className="bt-td-actions">
                                                <div className="bt-action-group">
                                                    {(status === 'waiting' || status === 'offline') && (
                                                        <button
                                                            className={`bt-action-btn bt-action-start ${status === 'offline' ? 'bt-action-disabled' : ''}`}
                                                            title={status === 'offline' ? 'Start VM first' : 'Start Bot'}
                                                            onClick={() => sendCommand(bot.sip_id, 'start')}
                                                            disabled={isProc || status === 'offline'}>
                                                            <Play size={11} />
                                                        </button>
                                                    )}
                                                    {(status === 'active' || status === 'running' || status === 'dial_level_0') && (
                                                        <button className="bt-action-btn bt-action-stop" title="Stop Bot"
                                                            onClick={() => sendCommand(bot.sip_id, 'stop')} disabled={isProc}>
                                                            <Square size={11} />
                                                        </button>
                                                    )}
                                                    <button className="bt-action-btn bt-action-stats" title="View Calls"
                                                        onClick={() => navigate(`/monitoring-v3?tab=call_logs&sip=${encodeURIComponent(bot.sip_id)}`)}>
                                                        <BarChart3 size={11} />
                                                    </button>
                                                    {/* v3.95: Crash Reports Button */}
                                                    <button
                                                        className={`bt-action-btn bt-action-crash ${bot.auto_revive_count > 0 ? 'bt-has-crashes' : ''}`}
                                                        title={bot.auto_revive_count > 0 ? `View Crash Reports (${bot.auto_revive_count} recent)` : 'View Crash Reports'}
                                                        onClick={() => setCrashReportBot(bot)}
                                                    >
                                                        <FileWarning size={11} />
                                                    </button>
                                                    {/* Voice Sets Button */}
                                                    <button
                                                        className="bt-action-btn bt-action-voice"
                                                        title="Edit Voice Sets"
                                                        onClick={() => openVoiceSetModal(bot)}
                                                    >
                                                        <Mic size={11} />
                                                    </button>
                                                    {/* Transcriber Button */}
                                                    <button
                                                        className="bt-action-btn bt-action-transcriber"
                                                        title="Change Transcriber"
                                                        onClick={() => openTranscriberModal(bot)}
                                                    >
                                                        <Radio size={11} />
                                                    </button>
                                                    {/* Live Logs Button - Only for dev/test mode bots */}
                                                    {(bot.bot_mode === 'development' || bot.bot_mode === 'test') && (
                                                        <button
                                                            className="bt-action-btn bt-action-logs"
                                                            title="Live Logs"
                                                            onClick={() => setLiveLogsBot(bot)}
                                                        >
                                                            <Terminal size={11} />
                                                        </button>
                                                    )}
                                                    {/* VM Expand Button */}
                                                    <button
                                                        className={`bt-action-btn bt-action-vm ${!bot.server_ip ? 'bt-action-disabled' : ''} ${bot.vmid ? 'bt-action-linked' : ''} ${isExpanded ? 'bt-action-expanded' : ''}`}
                                                        title={!bot.server_ip ? 'No server' : bot.vmid ? `VM ${bot.vmid}` : 'Link VM'}
                                                        onClick={() => toggleExpand(bot)}
                                                        disabled={isProc || !bot.server_ip}
                                                    >
                                                        <Server size={11} />
                                                        {isExpanded ? <ChevronUp size={9} /> : <ChevronDown size={9} />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Expanded VM Controls Row */}
                                        {isExpanded && (
                                            <tr key={`${bot.sip_id}-vm`} className="bt-vm-row">
                                                <td colSpan={18}>
                                                    <div className="bt-vm-panel">
                                                        <div className="bt-vm-panel-header">
                                                            <Server size={14} />
                                                            <span>VM Controls</span>
                                                            {bot.vmid && (
                                                                <span className="bt-vm-id">VM {bot.vmid}</span>
                                                            )}
                                                            {currentVmStatus?.loading && (
                                                                <span className="bt-vm-loading">Checking status...</span>
                                                            )}
                                                            {currentVmStatus?.status && !currentVmStatus.loading && (
                                                                <span className={`bt-vm-status bt-vm-status-${currentVmStatus.status}`}>
                                                                    {currentVmStatus.status === 'running' ? <Power size={12} /> : <PowerOff size={12} />}
                                                                    {currentVmStatus.status}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="bt-vm-panel-body">
                                                            {bot.vmid ? (
                                                                <div className="bt-vm-actions">
                                                                    {/* Show Start only when stopped */}
                                                                    {currentVmStatus?.status === 'stopped' && (
                                                                        <button className="bt-vm-action-btn bt-vm-start" onClick={() => vmAction('start', bot)}>
                                                                            <Play size={14} /> Start VM
                                                                        </button>
                                                                    )}
                                                                    {/* Show Stop and Reset when running */}
                                                                    {currentVmStatus?.status === 'running' && (
                                                                        <>
                                                                            <button className="bt-vm-action-btn bt-vm-stop" onClick={() => vmAction('stop', bot)}>
                                                                                <Square size={14} /> Stop VM
                                                                            </button>
                                                                            <button className="bt-vm-action-btn bt-vm-restart" onClick={() => vmAction('restart', bot)}>
                                                                                <RotateCcw size={14} /> Reset VM
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                    {/* Show all when status unknown */}
                                                                    {(!currentVmStatus?.status || currentVmStatus.status === 'unknown') && (
                                                                        <>
                                                                            <button className="bt-vm-action-btn bt-vm-start" onClick={() => vmAction('start', bot)}>
                                                                                <Play size={14} /> Start VM
                                                                            </button>
                                                                            <button className="bt-vm-action-btn bt-vm-stop" onClick={() => vmAction('stop', bot)}>
                                                                                <Square size={14} /> Stop VM
                                                                            </button>
                                                                            <button className="bt-vm-action-btn bt-vm-restart" onClick={() => vmAction('restart', bot)}>
                                                                                <RotateCcw size={14} /> Reset VM
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                    <button className="bt-vm-action-btn bt-vm-link" onClick={() => { setExpandedBot(null); setLinkVMBot({ ...bot, dialer_id: dialerId }) }}>
                                                                        <Link2 size={14} /> Change VM
                                                                    </button>
                                                                    <button
                                                                        className="bt-vm-action-btn bt-vm-clone"
                                                                        onClick={() => navigate(`/servers?ip=${bot.server_ip}&bulkClone=true`)}
                                                                        title="Clone from template"
                                                                    >
                                                                        <Copy size={14} /> Clone
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="bt-vm-no-link">
                                                                    <span>No VM linked to this bot</span>
                                                                    <button className="bt-vm-action-btn bt-vm-link" onClick={() => { setExpandedBot(null); setLinkVMBot({ ...bot, dialer_id: dialerId }) }}>
                                                                        <Link2 size={14} /> Link VM
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 0 && !loading && (
                <div className="bt-pagination">
                    <span className="bt-page-info">
                        {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, filteredBots.length)} of {filteredBots.length}
                    </span>
                    <div className="bt-page-controls">
                        <select
                            className="bt-select"
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}
                        >
                            {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                            <ChevronLeft size={14} />
                        </button>
                        <span>{currentPage}/{totalPages}</span>
                        <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
                            <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            )}

            {/* Link VM Modal */}
            {linkVMBot && (
                <LinkVMModal
                    bot={linkVMBot}
                    onClose={() => setLinkVMBot(null)}
                    onLinked={() => {
                        setLinkVMBot(null)
                        fetchBots()
                    }}
                />
            )}

            {/* v3.95: Crash Report Modal */}
            {crashReportBot && (
                <CrashReportModal
                    bot={crashReportBot}
                    onClose={() => setCrashReportBot(null)}
                />
            )}

            {/* Live Logs Modal - Dev mode bots only */}
            {liveLogsBot && (
                <LiveLogsModal
                    bot={liveLogsBot}
                    onClose={() => setLiveLogsBot(null)}
                />
            )}

            {/* Voice Sets Modal */}
            {showVoiceSetModal && (voiceSetEditBot || voiceSetBulkMode) && (
                <div className="bt-edit-modal-overlay" onClick={closeVoiceSetModal}>
                    <div className="bt-edit-modal" onClick={e => e.stopPropagation()}>
                        <div className="bt-edit-modal-header">
                            <h3>
                                <Mic size={18} />
                                {voiceSetBulkMode
                                    ? `Voice Sets: ${voiceSetBulkBots.length} Bot${voiceSetBulkBots.length > 1 ? 's' : ''}`
                                    : `Voice Sets: ${voiceSetEditBot?.sip_id}`
                                }
                            </h3>
                            <button className="bt-edit-modal-close" onClick={closeVoiceSetModal}>
                                <X size={18} />
                            </button>
                        </div>

                        {voiceSetEditLoading ? (
                            <div className="bt-edit-modal-loading">
                                <Loader2 size={24} className="spinning" />
                                <span>Loading voice sets...</span>
                            </div>
                        ) : (
                            <div className="bt-edit-modal-body">
                                {/* Voice Set Pool */}
                                <div className="bt-edit-section">
                                    <h4><Mic size={14} /> Voice Sets</h4>
                                    <p className="bt-edit-hint">Select voice sets to rotate through on each call. Click the play button to preview.</p>
                                    <div className="bt-voice-set-grid">
                                        {voiceSets.map(vs => (
                                            <div
                                                key={vs.id}
                                                className={`bt-voice-set-item ${licenseData.voice_set_pool?.includes(vs.id) ? 'selected' : ''}`}
                                                onClick={() => toggleVoiceSet(vs.id)}
                                            >
                                                <button
                                                    className="bt-vs-preview"
                                                    onClick={(e) => previewVoiceSet(vs.id, e)}
                                                    title="Preview voice"
                                                >
                                                    {audioLoading === vs.id ? (
                                                        <Loader2 size={12} className="spinning" />
                                                    ) : playingVoiceSet === vs.id ? (
                                                        <Pause size={12} />
                                                    ) : (
                                                        <Play size={12} />
                                                    )}
                                                </button>
                                                <span className="bt-vs-name">{vs.name}</span>
                                                {vs.gender && <span className="bt-vs-gender">{vs.gender}</span>}
                                                {licenseData.voice_set_pool?.includes(vs.id) && <Check size={12} className="bt-vs-check" />}
                                            </div>
                                        ))}
                                    </div>
                                    {licenseData.voice_set_pool?.length > 0 && (
                                        <div className="bt-selected-count">
                                            {licenseData.voice_set_pool.length} voice set{licenseData.voice_set_pool.length > 1 ? 's' : ''} selected
                                        </div>
                                    )}
                                </div>

                                {/* Restart notice */}
                                <div className="bt-edit-notice">
                                    <AlertCircle size={14} />
                                    <span>Changes will take effect after restarting the VM</span>
                                </div>
                            </div>
                        )}

                        <div className="bt-edit-modal-footer">
                            <button className="bt-edit-cancel" onClick={closeVoiceSetModal}>Cancel</button>
                            <button
                                className="bt-edit-save"
                                onClick={saveVoiceSetChanges}
                                disabled={voiceSetSaving || voiceSetEditLoading}
                            >
                                {voiceSetSaving ? <><Loader2 size={14} className="spinning" /> Saving...</> : <><Check size={14} /> Save Changes</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Transcriber Modal */}
            {showTranscriberModal && (transcriberEditBot || transcriberBulkMode) && (
                <div className="bt-edit-modal-overlay" onClick={closeTranscriberModal}>
                    <div className="bt-edit-modal bt-transcriber-modal" onClick={e => e.stopPropagation()}>
                        <div className="bt-edit-modal-header">
                            <h3>
                                <Radio size={18} />
                                {transcriberBulkMode
                                    ? `Transcriber: ${transcriberBulkBots.length} Bot${transcriberBulkBots.length > 1 ? 's' : ''}`
                                    : `Transcriber: ${transcriberEditBot?.sip_id}`
                                }
                            </h3>
                            <button className="bt-edit-modal-close" onClick={closeTranscriberModal}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="bt-edit-modal-body">
                            <div className="bt-edit-section">
                                <h4><Radio size={14} /> Select Transcriber</h4>
                                <p className="bt-edit-hint">Choose the speech recognition model for this bot.</p>
                                <div className="bt-transcriber-grid">
                                    {TRANSCRIBER_OPTIONS.map(opt => (
                                        <div
                                            key={opt.id}
                                            className={`bt-transcriber-option ${selectedTranscriber === opt.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedTranscriber(opt.id)}
                                        >
                                            <Radio size={14} />
                                            <span className="bt-to-label">{opt.label}</span>
                                            <span className="bt-to-desc">{opt.desc}</span>
                                            {selectedTranscriber === opt.id && <Check size={12} className="bt-to-check" />}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bt-edit-notice">
                                <AlertCircle size={14} />
                                <span>Changes will take effect after restarting the VM</span>
                            </div>
                        </div>

                        <div className="bt-edit-modal-footer">
                            <button className="bt-edit-cancel" onClick={closeTranscriberModal}>Cancel</button>
                            <button
                                className="bt-edit-save"
                                onClick={saveTranscriberChange}
                                disabled={transcriberSaving}
                            >
                                {transcriberSaving ? <><Loader2 size={14} className="spinning" /> Saving...</> : <><Check size={14} /> Save Changes</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
